import { Alert, Snackbar, Stack, Typography } from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from '~/components/atoms/Link';
import { ResetPasswordForm } from '~/components/organisms/ResetPasswordForm';
import { LOGIN_PATH } from '~/constants';
import { Logger } from '~/lib/logger';
import { useApi } from '~/providers/ApiProvider';
import type { ResetPasswordFormType } from '~/types/ResetPassword';

const logger = new Logger('ForgotPassword');

export const ResetPassword: FC = () => {
  const { warehouseApi } = useApi();
  const [searchParams] = useSearchParams();
  const payload = decodeURI(searchParams.get('payload') || '');

  const [showError, setShowError] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const { mutate, isPending } = warehouseApi.resetPassword.useMutation({
    onSuccess: () => {
      setShowSuccess(true);
    },
    onError: (error) => {
      logger.error('Error sending reset password email', error);
      setShowError(true);
    }
  });

  const onSubmit = (data: ResetPasswordFormType) => {
    if (!payload) {
      logger.error('Form submit error: payload not found in URL');
      return;
    }

    mutate({
      body: {
        data: {
          payload,
          password: data.password
        }
      }
    });
  };

  return (
    <>
      <Stack spacing={4.75} maxWidth={360} alignSelf="center">
        <Typography component="h1" variant="subtitle1" fontWeight={600} textAlign="center">
          Reset Password
        </Typography>

        <Typography variant="subtitle2" fontWeight={600} textAlign="center">
          Enter your new password below and confirm it to reset your password.
        </Typography>

        {showSuccess ? (
          <Typography
            variant="subtitle2"
            fontWeight={600}
            textAlign="center"
            color={({ palette }) => palette.success.main}
          >
            Return to the login page to sign in with your new password.
          </Typography>
        ) : (
          <ResetPasswordForm isPending={isPending} onSubmit={onSubmit} />
        )}

        <Typography
          id="reset_password_back_to_login_link"
          component={Link}
          to={LOGIN_PATH}
          variant="body2"
          fontWeight={600}
          textAlign="center"
        >
          Back to Login
        </Typography>
      </Stack>

      <Snackbar open={showError} autoHideDuration={4000} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="error" variant="standard" title="Unexpected error" onClose={() => setShowError(false)}>
          An unexpected error occurred. Please try again or contact support.
        </Alert>
      </Snackbar>
    </>
  );
};
